import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { pixelEnabled, readConsent, saveConsent, trackPixel, type Consent } from '../analytics/metaPixel';
import { PRIVACY_URL } from '@/config/legal';
import { Button } from '../ui';
import { announceConsent } from './consentEvents';

/**
 * Asks whether Meta may measure ads — and, once it may, sends the FIRST PageView.
 *
 * Only the first. Every later route change is sent by fbevents itself: once loaded it hooks
 * pushState, replaceState and popstate and tracks a PageView for each. It also drops every
 * explicit `fbq('track', 'PageView')` after the first for a pixel, unless
 * `fbq.allowDuplicatePageViews` is set — so a PageView sent here per route was a no-op on
 * every route but the first, and would become a DOUBLE count the day Meta stops deduping
 * (its `spa_pageview_fix` experiment already does). One explicit view per page load, then,
 * sent on the first route where consent is granted — including consent given mid-session.
 *
 * Do NOT set `fbq.disablePushState` to "fix duplicates" unless you also set
 * `fbq.allowDuplicatePageViews = true` and go back to sending a PageView per route here.
 * With the history hook off and the dedupe on, in-app navigation produces no PageViews at
 * all, and every funnel audience built on them empties without an error.
 *
 * A yes covers more than cookies: once the visitor signs in, Meta also gets a SHA-256 hash of
 * their email and account id, made in the browser (analytics/metaPixel.ts), and the events may
 * pass through Stape and a backup server on their way. The banner says both, and a yes stored
 * before it did reads as unanswered, so everyone is asked this question.
 *
 * Someone who said no on saddle-sites.com is not asked here: a no is a cookie on the shared
 * domain. A yes is not carried over, so someone who accepted there is asked once more here —
 * published apps share that domain and can write the cookie too, and a yes a stranger can
 * forge is no yes at all (see analytics/metaPixel.ts). People tapping an ad that points
 * straight at the builder never saw the landing banner either.
 */
export default function ConsentBanner() {
  const enabled = pixelEnabled();
  const [choice, setChoice] = useState<Consent | null>(() => (enabled ? readConsent() : null));
  const { search } = useLocation();
  const sent = useRef(false);
  // The landing hero hands the visitor's typed sentence over as ?idea=. fbevents sends the
  // page's full URL with every event, so no PageView goes while it is there: Idea takes it
  // out on arrival, and the view is sent from the clean URL a moment later. Only this param
  // — refusing on any query would also drop fbclid landings, the attribution itself.
  const carriesPrompt = new URLSearchParams(search).has('idea');

  useEffect(() => {
    if (choice !== 'granted' || carriesPrompt || sent.current) return;
    sent.current = true;
    trackPixel('PageView');
  }, [choice, carriesPrompt]);

  if (!enabled || choice) return null;

  const decide = (c: Consent) => {
    saveConsent(c);
    setChoice(c);
    // After it is saved, so a listener that sends an event finds the yes (consentEvents.ts, F23).
    announceConsent(c);
  };

  return (
    <div className="consent card clay-card" role="region" aria-label="Cookie consent">
      {/*
        Every recipient a yes sends to, named: Meta, and — while the dataset's live config
        relays through OpenBridge — Stape's gateway and its fallback on Google Cloud, which
        nobody has identified. scripts/verify-pixel-config.mjs reads this paragraph against the
        live config and says when a name here is missing or no longer true; take one out when
        it reports STALE. The second sentence is what a yes covers besides cookies: a signed-in
        visitor's email, hashed in this browser (analytics/metaPixel.ts). A yes given before
        either was here is asked again (CONSENT_VERSION). scripts/verify-web-deploy.sh looks
        for that sentence in the served bundle, so keep its marker in step if the words change.

        Short, because an ad's visitor meets it over the bottom of Sign up: only the words around
        the facts were cut (2026-10-01), never a fact. Pinned phrases stay verbatim: the relay
        sentence and the email sentence (consentBanner.test.tsx, verify-web-deploy.sh), and the
        opening "We use Meta's cookies" (scripts/verify-pixel-config.test.mjs).

        That makes it four lines at 390px, and four is the floor: the pinned phrases alone are
        about 140 characters, and 187 still wrapped to four. "One or two lines" was tried on
        2026-10-01 ("Meta cookies measure our ads, via Stape and a Google Cloud backup…") and
        verify-pixel-config reported SERIOUS on both relays. Sign up is laid out around this
        height; e2e-fold/signUpFold.spec.ts fails if it grows.
      */}
      <p className="type-caption">
        We use Meta&rsquo;s cookies to measure ads; the data may pass through Stape and a backup
        server on Google Cloud on its way to Meta.{' '}
        If you accept and sign in, Meta also gets a scrambled copy of your email.{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy</a>
      </p>
      {/*
        The same button twice. A solid orange Accept beside a white-on-white No thanks steered
        the answer, which is not consent; neither is the default here.
      */}
      <div className="consent-actions">
        <Button title="No thanks" variant="secondary" className="consent-choice" onPress={() => decide('denied')} />
        <Button title="Accept" variant="secondary" className="consent-choice" onPress={() => decide('granted')} />
      </div>
    </div>
  );
}
